import { getGetPosQueryKey, GetPosParams, PaginatedPosListItemsDTO, useDeletePosById, useGetPos } from "@bvhi/backend-api";
import { DialogContentText, GridPaginationModel, GridSortModel } from "@bvhi/ui-base";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIDataGrid } from "../../../../../components/datagrid/bvhi-datagrid";
import { useAuthContext } from "../../../../../context/use-auth-context";
import { SecuredDeleteDialog } from "../../../../../dialogs/secured-delete-dialog";
import { useToast } from "../../../../../hooks/use-toast";
import { toNarrowedTypeOrNull } from "../../../../../utils/ts";
import { useVerificationBatchSortParams } from "../../../verification-code/list/hooks/use-verification-batch-sort-params";
import { usePosDataGridDefinitions } from "../hooks/use-pos-datagrid-definitions";
interface Props {
  requestParams: GetPosParams;
  updateCallback: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
  mode?: "full" | "compact";
  pageSizeOptions?: number[];
}

export const PosDataGrid = ({ requestParams, updateCallback, mode = "full", pageSizeOptions = [10, 25, 50] }: Props) => {
  const { t } = useTranslation(["pos", "errors", "dialogs"]);
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { mutate: deletePosById } = useDeletePosById();
  const [posIdToDelete, setPosIdToDelete] = useState<string | null>(null);
  const { pushDeleteErrorToast, pushDeleteSuccessToast } = useToast();
  const { getSortModelForBackendString } = useVerificationBatchSortParams();

  const { data, isFetching } = useGetPos(currentUser?.organisation?.id ?? "", requestParams, {
    query: { throwOnError: (error) => error.status === 500 || error.status === 403, placeholderData: keepPreviousData },
  });

  const handleDelete = useCallback(
    (posId: string) => {
      setPosIdToDelete(posId);
    },
    [setPosIdToDelete]
  );

  const { columns } = usePosDataGridDefinitions(mode, handleDelete);

  const handleConfirmDialogClose = useCallback(
    (confirmed: boolean) => {
      if (!confirmed || !posIdToDelete) {
        setPosIdToDelete(null);
        return;
      }
      if (!currentUser?.organisation?.id) {
        pushDeleteErrorToast(t("pos:pos.entity"));
        return;
      }
      deletePosById(
        { audiologistId: currentUser?.organisation?.id, posId: posIdToDelete },
        {
          onSuccess: () => {
            pushDeleteSuccessToast(t("pos:pos.entity"));
          },
          onError: () => {
            pushDeleteErrorToast(t("pos:pos.entity"));
          },
          onSettled: () => {
            setPosIdToDelete(null);
            void queryClient.invalidateQueries({ queryKey: getGetPosQueryKey(currentUser?.organisation?.id ?? "") });
          },
        }
      );
    },
    [currentUser, deletePosById, posIdToDelete, pushDeleteErrorToast, pushDeleteSuccessToast, queryClient, t]
  );

  const pointOfSales = toNarrowedTypeOrNull<PaginatedPosListItemsDTO>(data?.data, "numberOfItems");

  return (
    <>
      <BVHIDataGrid
        columns={columns}
        initialSortModel={getSortModelForBackendString(requestParams.sort && requestParams.sort.length >= 1 ? requestParams.sort[0] : "")}
        loading={isFetching}
        pageSizeOptions={pageSizeOptions}
        rowCount={pointOfSales?.numberOfItems ?? -1}
        rows={pointOfSales?.payload ?? []}
        totalPages={pointOfSales?.numberOfPages ?? 0}
        updateCallback={updateCallback}
        initalPaginationModel={{
          page: requestParams.page ? requestParams.page : 0,
          pageSize: requestParams.size ? requestParams.size : 25,
        }}
      />
      <SecuredDeleteDialog
        onClose={handleConfirmDialogClose}
        open={posIdToDelete !== null}
        overwrite={{
          title: t("dialogs:dialogs.delete.pos.title"),
          additionalContent: <DialogContentText pb={3}>{t("dialogs:dialogs.delete.pos.additional.content")}</DialogContentText>,
        }}
      />
    </>
  );
};
